import React, { useEffect, useState } from 'react';
import { Building2, CalendarClock, KeyRound, Save, Store } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SaaSServices } from '@/services/saasServices';
import { supportGrantIsActive } from './saasAdministrationForm';
import { apiErrorMessage, readStoredInstitution, readStoredUser, unwrapApiData } from '@/auth/saasSession';
import {
  RETENTION_SUBJECT_OPTIONS,
} from '@/auth/saasCatalog';
import {
  formatInstitutionDateTime,
  institutionDateTimeInputValue,
  institutionLocalDateTimeToIso,
  resolvedInstitutionTimeZone,
} from '../uitilites/institutionTime';

const TABS = [
  { id: 'retention', label: 'Retention', icon: CalendarClock },
  { id: 'support', label: 'Support access', icon: KeyRound },
  { id: 'marketplace', label: 'Marketplace', icon: Store },
];

const Panel = ({ title, description, children, action }) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <header className="flex flex-col gap-3 border-b border-slate-200 px-6 py-5 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {action}
    </header>
    <div className="p-6">{children}</div>
  </section>
);

export const supportAccessExpiryLimits = (now = new Date(), timezone = '') => ({
  min: institutionDateTimeInputValue(new Date(now.getTime() + 60_000), timezone),
  max: institutionDateTimeInputValue(new Date(now.getTime() + (24 * 60 * 60_000)), timezone),
});

export default function AccessAdministration() {
  const user = readStoredUser();
  const activeInstitution = readStoredInstitution();
  const [tab, setTab] = useState('retention');
  const [loading, setLoading] = useState(true);
  const [retention, setRetention] = useState({ retention_days: {}, version: 0 });
  const [supportGrants, setSupportGrants] = useState([]);
  const [securityNotices, setSecurityNotices] = useState([]);
  const [marketplacePolicy, setMarketplacePolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [supportGrant, setSupportGrant] = useState({ reason: '', expires_at: '', read_only: true });
  const [institutionTimezone, setInstitutionTimezone] = useState(() => resolvedInstitutionTimeZone(
    activeInstitution?.timezone,
    user?.institution_timezone,
    user?.saas_context?.institution_timezone,
  ));

  const institutionName = activeInstitution?.display_name || user?.institution_name || 'Current institution';
  const supportExpiryLimits = supportAccessExpiryLimits(new Date(), institutionTimezone);
  const load = async () => {
    setLoading(true);
    const [retentionRes, grantsRes, noticesRes, marketplaceRes, institutionsRes] = await Promise.all([
      SaaSServices.getRetentionPolicy(),
      SaaSServices.listSupportGrants(),
      SaaSServices.listSecurityNotices(),
      SaaSServices.getMarketplacePolicy(),
      SaaSServices.listMyInstitutions(),
    ]);
    setRetention(unwrapApiData(retentionRes) || { retention_days: {}, version: 0 });
    setSupportGrants(unwrapApiData(grantsRes) || []);
    setSecurityNotices(unwrapApiData(noticesRes) || []);
    setMarketplacePolicy(unwrapApiData(marketplaceRes));
    const institutionRows = unwrapApiData(institutionsRes)?.institutions || [];
    const currentInstitution = institutionRows.find((institution) => (
      String(institution.id || '') === String(activeInstitution?.id || user?.institution_id || user?.saas_context?.institution_id || '')
    ));
    setInstitutionTimezone(resolvedInstitutionTimeZone(
      currentInstitution?.timezone,
      activeInstitution?.timezone,
      user?.institution_timezone,
      user?.saas_context?.institution_timezone,
    ));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveRetention = async () => {
    setSaving(true);
    const response = await SaaSServices.updateRetentionPolicy({
      retention_days: Object.fromEntries(RETENTION_SUBJECT_OPTIONS.map(({ value }) => (
        [value, Number(retention.retention_days?.[value] ?? 0)]
      ))),
      expected_version: retention.version || 0,
    });
    const saved = unwrapApiData(response);
    setSaving(false);
    if (!saved) return toast.error(apiErrorMessage(response));
    setRetention(saved);
    toast.success('Retention policy saved');
  };

  const createSupportGrant = async (event) => {
    event.preventDefault();
    const expiresAt = institutionLocalDateTimeToIso(
      supportGrant.expires_at,
      institutionTimezone,
    );
    if (!expiresAt) {
      toast.error(`Choose a valid time in ${institutionTimezone}.`);
      return;
    }
    setSaving(true);
    const response = await SaaSServices.createSupportGrant({
      reason: supportGrant.reason,
      expires_at: expiresAt,
      read_only: true,
    });
    const created = unwrapApiData(response);
    setSaving(false);
    if (!created) return toast.error(apiErrorMessage(response));
    setSupportGrants((rows) => [created, ...rows]);
    setSupportGrant({ reason: '', expires_at: '', read_only: true });
    toast.success(`Support request ${created.ticket_number} sent`);
  };

  if (loading) return <main className="flex h-full items-center justify-center text-sm text-slate-500">Loading access administration…</main>;

  return (
    <main className="h-full overflow-y-auto bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-slate-950 to-blue-950 px-6 py-6 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <span className="rounded-xl bg-white/10 p-3"><Building2 className="h-6 w-6" /></span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-200">Institution administration</p>
              <h1 className="mt-1 text-2xl font-bold">{institutionName}</h1>
              <p className="mt-1 text-sm text-slate-300">Institution role post-close access, controlled support access and marketplace governance.</p>
            </div>
          </div>
        </header>

        <nav className="flex gap-2 overflow-x-auto rounded-xl border border-slate-200 bg-white p-2" aria-label="Access administration sections">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} type="button" onClick={() => setTab(id)} className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${tab === id ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </nav>

        {tab === 'retention' && (
          <Panel title="Post-close read-only access" description="All event staffing labels, participants and leaders lose access at closure. These windows apply only to active Executive Oversight and Compliance Auditor institution members." action={<Button onClick={saveRetention} disabled={saving}><Save className="mr-2 h-4 w-4" /> Save access periods</Button>}>
            <div className="grid gap-4 md:grid-cols-2">
              {RETENTION_SUBJECT_OPTIONS.map((subject) => (
                <label key={subject.value} className="rounded-xl border border-slate-200 p-4">
                  <span className="font-semibold text-slate-900">{subject.label}</span>
                  <span className="mt-1 block min-h-10 text-xs text-slate-500">{subject.hint}</span>
                  <span className="mt-3 flex items-center gap-2"><Input type="number" min="0" value={retention.retention_days?.[subject.value] ?? 0} onChange={(event) => setRetention((old) => ({ ...old, retention_days: { ...(old.retention_days || {}), [subject.value]: Number(event.target.value) } }))} /><span className="text-sm text-slate-500">days</span></span>
                </label>
              ))}
            </div>
          </Panel>
        )}

        {tab === 'support' && (
          <div className="space-y-6">
            <Panel title="Request Platform Support" description="Describe the issue and choose when access should end. The SaaS support team will see the request automatically.">
              <form onSubmit={createSupportGrant} className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900 md:col-span-2"><strong>Support reference:</strong> DisasterMS creates it automatically when you send the request.</div>
                <label className="text-sm font-semibold text-slate-700 md:col-span-2">Access ends<Input className="mt-1" required type="datetime-local" min={supportExpiryLimits.min} max={supportExpiryLimits.max} value={supportGrant.expires_at} onChange={(event) => setSupportGrant((old) => ({ ...old, expires_at: event.target.value }))} /><span className="mt-1 block text-xs font-normal text-slate-500">Choose when support access must stop within the next 24 hours. After this time, support can no longer open your institution. Timezone: <strong>{institutionTimezone}</strong>.</span></label>
                <label className="text-sm font-semibold text-slate-700 md:col-span-2">What do you need help with?<Input className="mt-1" required minLength={3} maxLength={500} placeholder="Describe the problem for the support team" value={supportGrant.reason} onChange={(event) => setSupportGrant((old) => ({ ...old, reason: event.target.value }))} /></label>
                <div className="md:col-span-2 flex flex-col gap-3 rounded-lg bg-blue-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm text-blue-900">Support can review this institution only. Access is read-only, audited and ends automatically.</span>
                  <Button className="shrink-0" type="submit" disabled={saving}>Send support request</Button>
                </div>
              </form>
            </Panel>
            <Panel title="Support access history" description="Past and cancelled support access remains visible for audit purposes.">
              <div className="space-y-3">
                {supportGrants.map((grant) => {
                  const active = supportGrantIsActive(grant);
                  const closed = Boolean(grant.closed_at);
                  const status = closed
                    ? 'Resolved'
                    : !active
                    ? (grant.revoked_at
                      ? 'Revoked'
                      : new Date(grant.expires_at).getTime() <= Date.now()
                        ? 'Expired'
                        : 'Unavailable')
                    : grant.support_user_id ? 'Support assigned' : 'Waiting for support';
                  return <div key={grant.id} className="rounded-xl border border-slate-200 p-4"><div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"><div><p className="font-semibold text-slate-900">{grant.ticket_number}</p><p className="text-sm text-slate-500">{grant.reason}</p><p className="mt-1 text-xs text-slate-400">Access ends {formatInstitutionDateTime(grant.expires_at, grant.institution_timezone || institutionTimezone)}</p>{closed && <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"><p className="text-xs font-bold uppercase tracking-wide text-emerald-800">Resolution</p><p className="mt-1 text-sm text-emerald-950">{grant.resolution || grant.resolution_summary}</p><p className="mt-1 text-xs text-emerald-700">Closed {formatInstitutionDateTime(grant.closed_at, grant.institution_timezone || institutionTimezone)}{grant.closed_by_name ? ` by ${grant.closed_by_name}` : ''}</p></div>}</div><div className="flex items-center gap-2"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${closed ? 'bg-emerald-100 text-emerald-700' : active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{status}</span>{active && !closed && <Button size="sm" variant="outline" onClick={async () => { const result = await SaaSServices.revokeSupportGrant(grant.id); if (unwrapApiData(result)) { setSupportGrants((rows) => rows.map((row) => row.id === grant.id ? { ...row, revoked_at: new Date().toISOString() } : row)); toast.success('Support access revoked'); } else toast.error(apiErrorMessage(result)); }}>Cancel access</Button>}</div></div></div>;
                })}
                {!supportGrants.length && <p className="text-sm text-slate-500">No support requests have been sent.</p>}
              </div>
            </Panel>
            {securityNotices.length > 0 && <Panel title="Security notices" description="Emergency platform actions are visibly reported to this institution."><div className="space-y-3">{securityNotices.map((notice) => <article key={notice.id} className="rounded-xl border border-red-200 bg-red-50 p-4"><p className="font-bold text-red-900">{notice.title}</p><p className="mt-1 text-sm text-red-800">Ticket {notice.detail?.ticket_number || '—'} · {notice.detail?.reason || notice.category}</p><p className="mt-1 text-xs text-red-600">{new Date(notice.created_at).toLocaleString()}</p></article>)}</div></Panel>}
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"><strong>Emergency break-glass:</strong> exceptional access requires a separate platform-controlled, visibly disclosed approval flow. It is not available through ordinary support requests.</div>
          </div>
        )}

        {tab === 'marketplace' && (
          <Panel title="Scenario marketplace sharing" description="The SaaS platform defines whether institutions may publish or import templates.">
            {marketplacePolicy ? (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 p-4"><p className="text-sm font-semibold text-slate-900">Publishing</p><p className="mt-1 text-sm text-slate-500">{marketplacePolicy.can_publish ? 'Allowed for this institution' : 'Disabled by platform policy'}</p></div>
                  <div className="rounded-xl border border-slate-200 p-4"><p className="text-sm font-semibold text-slate-900">Importing</p><p className="mt-1 text-sm text-slate-500">{marketplacePolicy.enabled ? 'Published templates may be imported' : 'Disabled by platform policy'}</p></div>
                </div>
                <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">Scenario content remains private unless an authorized administrator explicitly publishes a template and any required platform approval is completed.</p>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center"><Store className="mx-auto h-8 w-8 text-slate-400" /><p className="mt-3 font-semibold text-slate-700">Marketplace sharing is not enabled</p><p className="mt-1 text-sm text-slate-500">All scenarios remain private to this institution.</p></div>
            )}
          </Panel>
        )}
      </div>
    </main>
  );
}
